import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ChevronDown, ExternalLink } from "lucide-react";
import { CA, GMGN_URL, X_URL, SHORTEN } from "../config";
import { scrollToId } from "../lib/scroll";
import { copyCa } from "./CopyButton";
import Mascot from "./Mascot";
import Particles from "./Particles";
import { EASE } from "./Reveal";

function MaskedLine({ children, delay, className = "" }) {
    return (
        <span className={`block overflow-hidden ${className}`}>
            <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero() {
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 55, damping: 16 });
    const sy = useSpring(my, { stiffness: 55, damping: 16 });
    const pigeonX = useTransform(sx, [-0.5, 0.5], [20, -20]);
    const pigeonY = useTransform(sy, [-0.5, 0.5], [12, -12]);
    const pigeonR = useTransform(sx, [-0.5, 0.5], [2.5, -2.5]);
    const ringX = useTransform(sx, [-0.5, 0.5], [-14, 14]);
    const ringY = useTransform(sy, [-0.5, 0.5], [-10, 10]);

    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative flex min-h-screen items-center overflow-hidden pt-24 pb-16"
            onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                mx.set((e.clientX - r.left) / r.width - 0.5);
                my.set((e.clientY - r.top) / r.height - 0.5);
            }}
        >
            {/* Background layers */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_15%_10%,rgba(0,240,255,0.10),transparent),radial-gradient(ellipse_55%_55%_at_88%_70%,rgba(168,85,247,0.13),transparent)]" />
            <div className="grid-floor absolute inset-x-[-20%] bottom-[-8%] h-[46vh] mask-fade-b opacity-70" />
            <Particles className="absolute inset-0" density={52} />

            <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:gap-4 lg:px-10">
                {/* Copy */}
                <div className="order-2 lg:order-1 lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                        className="mb-6 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-4 py-1.5"
                        data-testid="hero-badge"
                    >
                        <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse-dot" />
                        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-cyan-300">
                            SOLANA MEMECOIN
                        </span>
                    </motion.div>

                    <h1 className="headline text-white">
                        <MaskedLine delay={0.18}>
                            <span className="text-gradient block text-6xl sm:text-7xl lg:text-8xl">$BALD</span>
                        </MaskedLine>
                        <MaskedLine delay={0.34} className="mt-2">
                            <span className="text-3xl sm:text-5xl lg:text-6xl">JUST A BALD PIGEON</span>
                        </MaskedLine>
                        <MaskedLine delay={0.48}>
                            <span className="text-3xl sm:text-5xl lg:text-6xl">
                                ON <span className="text-gradient">SOLANA.</span>
                            </span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
                        className="mt-6 max-w-xl text-base md:text-lg text-slate-400"
                    >
                        No complicated story.
                        <br />
                        No fake promises.
                        <br />
                        Just memes, community and one very bald pigeon.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
                        className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center"
                    >
                        <a
                            href={GMGN_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-buy-button"
                            className="btn-primary w-full sm:w-auto"
                        >
                            BUY $BALD <ExternalLink className="h-4 w-4" />
                        </a>
                        <a
                            href={X_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-join-community-button"
                            className="btn-outline-purple w-full sm:w-auto"
                        >
                            JOIN THE COMMUNITY
                        </a>
                        <button
                            type="button"
                            data-testid="hero-copy-ca-button"
                            onClick={copyCa}
                            className="btn-ghost w-full sm:w-auto"
                        >
                            COPY CA
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.05 }}
                        className="mt-6 flex items-center gap-2 font-mono text-xs md:text-sm text-slate-500"
                        data-testid="hero-ca-line"
                    >
                        <span className="shrink-0 uppercase tracking-widest">CA:</span>
                        <button
                            type="button"
                            data-testid="hero-ca-copy-inline"
                            onClick={copyCa}
                            className="truncate text-cyan-300/90 underline-offset-4 hover:underline"
                            title="Click to copy"
                        >
                            {SHORTEN(CA, 18, 10)}
                        </button>
                    </motion.div>
                </div>

                {/* Mascot */}
                <div className="order-1 lg:order-2 lg:col-span-5">
                    <motion.div
                        style={{ x: pigeonX, y: pigeonY, rotate: pigeonR }}
                        initial={{ opacity: 0, x: 130 }}
                        animate={{ opacity: 1, x: 0, y: 0 }}
                        transition={{ duration: 1.15, delay: 0.35, ease: EASE }}
                        className="relative mx-auto max-w-md lg:max-w-none"
                        data-testid="hero-mascot"
                    >
                        <motion.div
                            style={{ x: ringX, y: ringY }}
                            aria-hidden="true"
                            className="absolute left-1/2 top-1/2 -z-10 h-[86%] w-[86%] -translate-x-1/2 -translate-y-1/2 rounded-full"
                        >
                            <div className="absolute inset-0 rounded-full border-2 border-cyan-400/25 blur-[1px]" />
                            <div className="absolute inset-[6%] rounded-full bg-[conic-gradient(from_0deg,rgba(0,240,255,0.22),rgba(168,85,247,0.28),rgba(0,240,255,0.06),rgba(0,240,255,0.22))] blur-2xl animate-spin-slow" />
                        </motion.div>
                        <div className="relative overflow-hidden rounded-[2.5rem]">
                            <Mascot
                                name="hero"
                                eager
                                className="mx-auto w-full max-w-[420px] drop-shadow-[0_24px_60px_rgba(0,240,255,0.18)]"
                            />
                            <div className="shine-sweep" aria-hidden="true" />
                        </div>
                        <div className="pointer-events-none absolute inset-0 -z-10 rounded-[2.5rem] bg-[radial-gradient(circle_at_50%_45%,rgba(168,85,247,0.16),transparent_65%)]" />
                    </motion.div>
                </div>
            </div>

            <motion.button
                type="button"
                data-testid="hero-scroll-cue"
                onClick={() => scrollToId("about")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.8 }}
                className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-slate-500 hover:text-cyan-300"
                aria-label="Scroll to about section"
            >
                <ChevronDown className="h-7 w-7 animate-bounce" />
            </motion.button>
        </section>
    );
}
